import { getLocalStorage } from '@functions/localStorage';
import useIsSingleQuestPage from '@store/appRouter/hooks/useIsSingleQuestPage';
import useAddress from '@store/web3Store/hooks/useAddress';
import useConnectWallet from '@store/web3Store/hooks/useConnectWallet';
import useDisconnectWallet from '@store/web3Store/hooks/useDisconnectWallet';
import useIsWalletConnected from '@store/web3Store/hooks/useIsWalletConnected';
import useProvider from '@store/web3Store/hooks/useProvider';
import useWalletId from '@store/web3Store/hooks/useWalletId';
import { EWalletId, EWalletType } from '@store/web3Store/web3Store.enums';
import { getWalletFromId } from '@store/web3Store/web3Store.functions';
import {
	CONNECTED_WEB3_ACCOUNTS_KEY,
	SAVED_USER_ADDRESS_KEY,
	WALLETS_ARRAY
} from '@store/web3Store/web3Store.variables';
import { useCallback, useEffect, useRef } from 'react';
import useStarknetWallets from './useStarknetWallets';

export default function useStarknetState() {
	const walletType = EWalletType.starknet;

	const [address] = useAddress(walletType);
	const [provider] = useProvider(walletType);
	const [isWalletConnected] = useIsWalletConnected(walletType);
	const { disconnectWallet } = useDisconnectWallet(walletType);
	const [isSingleQuestPage] = useIsSingleQuestPage();
	const { connectWallet } = useConnectWallet();
	const [starknetWallets] = useStarknetWallets();
	const [walletId] = useWalletId(walletType);

	const refreshTimeout = useRef<NodeJS.Timeout>();

	const refreshAccount = useCallback(() => {
		refreshTimeout.current = setTimeout(() => {
			if (!walletId) return;

			// save wallet type
			const savedWallet = getWalletFromId(walletId);

			// disconnect wallet
			disconnectWallet();

			if (!savedWallet) return;

			// connect again (the idea is to update the provider )
			connectWallet(savedWallet);
		}, 200);
	}, [connectWallet, disconnectWallet, walletId]);

	// check if the user was previously connected and connect them if true (after page refresh for example)
	const checkIfWalletIsConnected = useCallback(() => {
		if (starknetWallets.state !== 'hasData') return;
		const walletTypeIds = WALLETS_ARRAY.filter((wallet) => wallet.type === walletType).map((wallet) => wallet.id);

		const connectedWeb3AccountsId = getLocalStorage<EWalletId[]>(CONNECTED_WEB3_ACCOUNTS_KEY) || [];

		const savedWalletId = walletTypeIds.find((walletId) => connectedWeb3AccountsId.includes(walletId));

		if (!savedWalletId) return;

		const savedWallet = getWalletFromId(savedWalletId);

		if (savedWallet) connectWallet(savedWallet);
	}, [connectWallet, starknetWallets.state, walletType]);

	useEffect(() => {
		// check if address is updated and disconnect the user if true
		if (address?.toLowercase && isWalletConnected) {
			const savedAddress = getLocalStorage(SAVED_USER_ADDRESS_KEY);

			if (savedAddress && savedAddress !== address?.toLowercase()) {
				disconnectWallet();
			}
		}
	}, [address, disconnectWallet, isWalletConnected]);

	useEffect(() => {
		if (!provider?.web3Instance) return;

		try {
			if (!provider.web3Instance.on) {
				console.warn('provider.web3Instance.on is undefined');
				return;
			}

			console.log('set starknet web3 listeners');
			provider.web3Instance.on('networkChanged', refreshAccount);
			provider.web3Instance.on('accountsChanged', refreshAccount);
		} catch (err) {
			console.error('Error while initializing web3 listeners', err);
		}

		return () => {
			if (!provider.web3Instance?.off) {
				console.warn('provider.web3Instance.off is undefined');
				return;
			}

			console.log('remove starknet web3 listeners');
			provider.web3Instance.off('networkChanged', refreshAccount);
			provider.web3Instance.off('accountsChanged', refreshAccount);
		};
	}, [provider?.web3Instance, refreshAccount]);

	useEffect(() => {
		if (!isSingleQuestPage) return;
		checkIfWalletIsConnected();
	}, [checkIfWalletIsConnected, isSingleQuestPage]);

	return null;
}
